import React from 'react';
import { 
  Paper, 
  Grid, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Box,
  Chip,
  Avatar
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import PendingIcon from '@mui/icons-material/Pending';

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed': return 'success';
    case 'ongoing': return 'warning';
    case 'upcoming': return 'info';
    default: return 'default';
  }
};

const getStatusIcon = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed': return <CheckCircleIcon fontSize="small" />;
    case 'ongoing': return <PlayCircleIcon fontSize="small" />;
    case 'upcoming': return <PendingIcon fontSize="small" />;
    default: return <EventIcon fontSize="small" />;
  }
};

const formatDateRange = (start, end) => {
  if (!start || !end) return 'No dates set';
  
  const startDate = new Date(start);
  const endDate = new Date(end);
  const options = { month: 'short', day: 'numeric' };
  
  if (startDate.getFullYear() !== endDate.getFullYear()) {
    return `${startDate.toLocaleDateString('en-US', { ...options, year: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { ...options, year: 'numeric' })}`;
  } else if (startDate.getMonth() !== endDate.getMonth()) {
    return `${startDate.toLocaleDateString('en-US', options)} - ${endDate.toLocaleDateString('en-US', options)}, ${endDate.getFullYear()}`;
  } else {
    return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.getDate()}, ${endDate.getFullYear()}`;
  }
};

export default function EventOverview({ events, selectedEventId, onEventChange }) {
  // If no event is selected and we have events, select the first one
  React.useEffect(() => {
    if (!selectedEventId && events && events.length > 0) {
      onEventChange(events[0]._id || events[0].id);
    }
  }, [selectedEventId, events, onEventChange]);

  // Handle empty events array
  if (!events || events.length === 0) {
    return (
      <Paper sx={{ 
        p: 3, 
        mb: 3, 
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        border: '1px solid rgba(0,0,0,0.06)',
        textAlign: 'center'
      }}>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          No Events Available
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create your first event to see it here
        </Typography>
      </Paper>
    );
  }

  // Find selected event, fallback to first event
  const event = events.find(e => (e._id || e.id) === selectedEventId) || events[0];

  if (!event) {
    return null;
  }
  
  return (
    <Paper sx={{ 
      p: 3, 
      mb: 3, 
      borderRadius: 3,
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
      border: '1px solid rgba(0,0,0,0.06)',
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 4,
        background: 'linear-gradient(90deg, #6366f1, #8b5cf6, #ec4899)'
      }
    }}>
      <Grid container spacing={3} alignItems="center" justifyContent="space-between">
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel sx={{ fontWeight: 500 }}>Event</InputLabel>
            <Select
              value={selectedEventId || ''}
              onChange={e => onEventChange(e.target.value)}
              label="Event"
              sx={{
                borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0,0,0,0.1)'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#6366f1'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#6366f1',
                  borderWidth: 2
                },
                '& .MuiSelect-select': {
                  fontWeight: 600
                }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: 2,
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                    '& .MuiMenuItem-root': {
                      borderRadius: 1,
                      mx: 1,
                      my: 0.5,
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: 'rgba(99, 102, 241, 0.08)'
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(99, 102, 241, 0.12)',
                        '&:hover': {
                          backgroundColor: 'rgba(99, 102, 241, 0.16)'
                        }
                      }
                    }
                  }
                }
              }}
            >
              {events.map(eventItem => (
                <MenuItem key={eventItem._id || eventItem.id} value={eventItem._id || eventItem.id}>
                  {eventItem.name} 
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={8}>
          <Grid container spacing={3}>
            <Grid item xs={6} md={3}>
              <Box sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: 'rgba(99, 102, 241, 0.05)',
                border: '1px solid rgba(99, 102, 241, 0.1)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.15)'
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Avatar sx={{
                    bgcolor: '#6366f1',
                    width: 28,
                    height: 28,
                    boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)'
                  }}>
                    <EventIcon fontSize="small" />
                  </Avatar>
                  <Typography variant="subtitle2" sx={{ 
                    color: 'text.secondary', 
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    fontSize: '0.75rem'
                  }}>
                    Date
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={700} sx={{ color: '#1a1a1a' }}>
                  {formatDateRange(event.startDate, event.endDate)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: 'rgba(139, 92, 246, 0.05)',
                border: '1px solid rgba(139, 92, 246, 0.1)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(139, 92, 246, 0.15)'
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Avatar sx={{
                    bgcolor: '#8b5cf6',
                    width: 28,
                    height: 28,
                    boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)'
                  }}>
                    <LocationOnIcon fontSize="small" />
                  </Avatar>
                  <Typography variant="subtitle2" sx={{ 
                    color: 'text.secondary', 
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    fontSize: '0.75rem'
                  }}>
                    Location
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={700} sx={{ color: '#1a1a1a' }}>
                  {event.location || 'No location set'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: 'rgba(34, 197, 94, 0.05)',
                border: '1px solid rgba(34, 197, 94, 0.1)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(34, 197, 94, 0.15)'
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Avatar sx={{
                    bgcolor: getStatusColor(event.status) === 'success' ? '#22c55e' : 
                             getStatusColor(event.status) === 'warning' ? '#f59e0b' :
                             getStatusColor(event.status) === 'info' ? '#3b82f6' : '#6b7280',
                    width: 28,
                    height: 28,
                    boxShadow: `0 2px 8px ${
                      getStatusColor(event.status) === 'success' ? 'rgba(34, 197, 94, 0.3)' : 
                      getStatusColor(event.status) === 'warning' ? 'rgba(245, 158, 11, 0.3)' :
                      getStatusColor(event.status) === 'info' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(107, 114, 128, 0.3)'
                    }`
                  }}>
                    {getStatusIcon(event.status)}
                  </Avatar>
                  <Typography variant="subtitle2" sx={{ 
                    color: 'text.secondary', 
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    fontSize: '0.75rem'
                  }}>
                    Status
                  </Typography>
                </Box>
                <Chip
                  label={event.status || 'Unknown'}
                  color={getStatusColor(event.status)}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    borderRadius: 2
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
}